import React, { createContext, useState, useEffect, useContext, useMemo } from 'react';

const CookiebotContext = createContext();

export const useCookiebot = () => useContext(CookiebotContext);

// Provider component that sets up the context
export const CookiebotProvider = ({ children }) => {
  const [cookieConsent, setCookieConsent] = useState({});

  // Calculate various values based on consent states

  const isPreferencesAllowed = useMemo(() => {
    return cookieConsent?.preferences ?? false;
  }, [cookieConsent?.preferences]);

  const isStatisticsAllowed = useMemo(() => {
    // Cookiebot's `statistics` flag is `false` both before the visitor has
    // responded to the banner and after they've explicitly rejected it, so it
    // can't be used alone to distinguish those cases. `method` is only set
    // once Cookiebot has recorded an actual decision (explicit or implicit) —
    // once it has, that decision is authoritative. Only fall back to Google
    // Consent Mode's regional default for visitors who haven't interacted
    // with the banner at all.
    if (cookieConsent?.method) {
      return cookieConsent.statistics === true;
    }
    return window.google_tag_data?.ics?.entries?.analytics_storage?.default === true;
  }, [cookieConsent?.method, cookieConsent?.statistics]);

  const isMarketingAllowed = useMemo(() => {
    return cookieConsent?.marketing ?? false;
  }, [cookieConsent?.marketing]);

  useEffect(() => {
    const handleConsent = () => {
      setCookieConsent({ ...window.Cookiebot?.consent });
    };

    if (window.Cookiebot) {
      handleConsent();
    }

    window.addEventListener('CookiebotOnConsentReady', handleConsent);

    return () => {
      window.removeEventListener('CookiebotOnConsentReady', handleConsent);
    };
  }, []);

  return (
    <CookiebotContext.Provider value={{
      cookieConsent,
      isPreferencesAllowed,
      isStatisticsAllowed,
      isMarketingAllowed,
    }}>
      {children}
    </CookiebotContext.Provider>
  );
};
